import React, { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error,
  autoFocus = true,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Split value into array for display
  const valueArray = value.split('').slice(0, length);
  while (valueArray.length < length) {
    valueArray.push('');
  }

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    // Call onComplete when all digits are filled
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const handleChange = (index: number, digit: string) => {
    if (disabled) return;

    // Only allow digits
    if (!/^\d*$/.test(digit)) return;

    const newValue = valueArray.slice();
    newValue[index] = digit.slice(-1); // Only take last character if multiple

    const newValueString = newValue.join('');
    onChange(newValueString);

    // Move to next input if digit entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      
      if (valueArray[index]) {
        // Clear current cell
        const newValue = valueArray.slice();
        newValue[index] = '';
        onChange(newValue.join(''));
      } else if (index > 0) {
        // Move to previous cell and clear it
        const newValue = valueArray.slice();
        newValue[index - 1] = '';
        onChange(newValue.join(''));
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    
    if (pastedData) {
      onChange(pastedData);
      
      // Focus last filled input or last input
      const focusIndex = Math.min(pastedData.length, length) - 1;
      if (focusIndex >= 0) {
        inputRefs.current[focusIndex]?.focus();
        setActiveIndex(focusIndex);
      }
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
    // Select the content when focused
    inputRefs.current[index]?.select();
  };

  return (
    <div className="w-full">
      <div className="flex justify-center gap-2 sm:gap-3">
        {valueArray.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={`
              w-10 h-12 sm:w-12 sm:h-14 
              text-center text-xl sm:text-2xl font-bold
              border-2 rounded-lg
              transition-all duration-200
              focus:outline-none
              ${disabled 
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                : error
                  ? 'border-red-500 bg-red-50 text-red-600 focus:border-red-600 focus:ring-2 focus:ring-red-200'
                  : activeIndex === index
                    ? 'border-primary bg-primary/5 text-white focus:ring-2 focus:ring-primary/30'
                    : digit
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-dark-600 bg-dark-700 text-white hover:border-dark-500 focus:border-primary focus:ring-2 focus:ring-primary/30'
              }
            `}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>
      
      {error && (
        <p className="mt-2 text-center text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default OtpInput;
